import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { MessageTranslator } from '@oneflowab/pomes';

import { getCurrentWorkspaceSelector } from '@app/reducers/app';
import { getAllWorkspacesFromSessionSelector } from '@app/reducers/session';
import { useSwitchWorkspace } from '@app/hooks/use-switch-workspace';

import CheckIcon from '@app/components/icons/new-check';
import WorkspaceIcon from '@app/components/icons/workspace';

import style from './switch-workspace.module.scss';

import CommandItem from '../item';

type Props = {
  message: MessageTranslator;
  closeCmdk: () => void;
  pushPage: (page: string) => void;
};

export const SwitchWorkspace = ({ message, closeCmdk }: Props) => {
  const currentWorkspace = useSelector(getCurrentWorkspaceSelector);
  const workspaces = useSelector(getAllWorkspacesFromSessionSelector);

  const switchWorkspace = useSwitchWorkspace();

  const handleSelect = useCallback(
    (workspaceId: number) => {
      switchWorkspace(workspaceId);
      closeCmdk();
    },
    [
      switchWorkspace,
      closeCmdk,
    ],
  );

  const getWorkspaceName = useCallback(
    (workspace: Oneflow.Workspace) => {
      if (workspace.virtual) {
        return message({
          id: 'Shared with me',
          comment: 'The virtual workspace name',
        }) as string;
      }
      return workspace.name || `Workspace ${workspace.id}`;
    },
    [
      message,
    ],
  );

  if (!workspaces) {
    return null;
  }

  return (
    <>
      {workspaces.map((workspace) => {
        const isCurrentWorkspace = currentWorkspace?.id === workspace.id;
        const workspaceName = getWorkspaceName(workspace);
        return (
          <CommandItem
            key={workspace.id}
            id={`workspace-${workspace.id}`}
            value={workspace.id.toString()}
            keywords={[
              workspaceName,
            ]}
            onSelect={() => handleSelect(workspace.id)}
          >
            <WorkspaceIcon
              height="16px"
              width="16px"
            />
            <span>{workspaceName}</span>
            {isCurrentWorkspace && (
              <CheckIcon
                height="16px"
                width="16px"
                className={style.CheckIcon}
              />
            )}
          </CommandItem>
        );
      })}
    </>
  );
};
